import React from 'react';
import { useAlerts, OverlayAlert } from '../../contexts/AlertContext';
import { AlertTriangle, X, Bell, ShieldAlert, Info, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';

const getPriorityStyles = (priority: OverlayAlert['priority']) => {
    switch (priority) {
        case 'critical':
            return {
                bg: 'bg-red-600',
                border: 'border-red-700',
                text: 'text-white',
                icon: ShieldAlert,
                iconColor: 'text-white',
            };
        case 'high':
            return {
                bg: 'bg-orange-500',
                border: 'border-orange-600',
                text: 'text-white',
                icon: AlertTriangle,
                iconColor: 'text-white',
            };
        case 'normal':
            return {
                bg: 'bg-blue-500',
                border: 'border-blue-600',
                text: 'text-white',
                icon: Bell,
                iconColor: 'text-white',
            };
        case 'low':
        default:
            return {
                bg: 'bg-gray-600',
                border: 'border-gray-700',
                text: 'text-white',
                icon: Info,
                iconColor: 'text-white',
            };
    }
};

const AlertCard: React.FC<{ alert: OverlayAlert; onDismiss: () => void }> = ({ alert, onDismiss }) => {
    const navigate = useNavigate();
    const styles = getPriorityStyles(alert.priority);
    const IconComponent = styles.icon;

    const handleAction = () => {
        if (alert.action?.url) {
            navigate(alert.action.url);
            onDismiss();
        }
    };

    return (
        <div
            className={`
        ${styles.bg} ${styles.border} ${styles.text}
        rounded-lg shadow-2xl border-2 p-4 min-w-[320px] max-w-[420px]
        animate-in slide-in-from-right-5 fade-in duration-300
      `}
        >
            <div className="flex items-start gap-3">
                <div className={`${styles.iconColor} mt-0.5`}>
                    <IconComponent className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <h4 className="font-semibold text-sm truncate">{alert.title}</h4>
                        <button
                            onClick={onDismiss}
                            className="p-1 hover:bg-white/20 rounded transition-colors flex-shrink-0"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <p className="text-sm mt-1 opacity-90 line-clamp-2">{alert.message}</p>
                    {alert.action && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleAction}
                            className="mt-3 gap-1"
                        >
                            {alert.action.label}
                            <ExternalLink className="h-3 w-3" />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export const AlertOverlay: React.FC = () => {
    const { activeAlerts, dismissAlert } = useAlerts();

    if (activeAlerts.length === 0) {
        return null;
    }

    return (
        <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none">
            {activeAlerts.map((alert) => (
                <div key={alert.id} className="pointer-events-auto">
                    <AlertCard alert={alert} onDismiss={() => dismissAlert(alert.id)} />
                </div>
            ))}
        </div>
    );
};

export default AlertOverlay;
